import { useState } from 'react';
import { Alert, AlertDescription } from '@xiltrion/ui/alert';
import { Button } from '@xiltrion/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@xiltrion/ui/dialog';
import { Icono } from '@xiltrion/ui/icono';
import { Input } from '@xiltrion/ui/input';
import { Field } from '@/components/ui';
import { Spinner } from '@xiltrion/ui/spinner';
import { Textarea } from '@xiltrion/ui/textarea';
import { avisar } from '@xiltrion/ui/sonner';
import { retryFailedEmailSchema } from '@pqrs/contracts';
import { errorMessage } from '@/features/cases/lib';
import { useResolveEmail, useRetryEmail } from './api';

const retried = (r: { status: string }) => (r.status === 'SENT' ? avisar.success('Correo enviado') : r.status === 'FAILED' ? avisar.error('Volvió a fallar', { description: 'Revisa el motivo y la configuración del correo saliente.' }) : avisar.info('Correo en cola de envío'));

/**
 * «Reenviar», «Reenviar a otro correo» y «Marcar como resuelto» de un correo al ciudadano que no salió (en la lista
 * y en Comunicaciones). `to`: a quién va ahora.
 */
export function FailedEmailActions({ id, to, size = 'sm' }: { id: string; to: string[]; size?: 'sm' | 'xs' }) {
  const retry = useRetryEmail();
  const resolve = useResolveEmail();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [other, setOther] = useState(false);
  const [address, setAddress] = useState('');
  const doRetry = () => retry.mutate({ id }, { onSuccess: retried, onError: (e) => avisar.error('No pudimos reenviarlo', { description: errorMessage(e) }) });
  const parsed = retryFailedEmailSchema.safeParse({ to: address });
  const same = parsed.success && to.length === 1 && to[0]!.toLowerCase() === parsed.data.to;
  return (
    <>
      <span className="inline-flex flex-wrap gap-2">
        <Button size={size} variant="outline" disabled={retry.isPending} onClick={doRetry}>{retry.isPending && !other ? <Spinner /> : <Icono nombre="mdi:email-sync-outline" />} Reenviar</Button>
        <Button size={size} variant="outline" disabled={retry.isPending} onClick={() => { setAddress(''); retry.reset(); setOther(true); }}><Icono nombre="mdi:email-edit-outline" /> Reenviar a otro correo</Button>
        <Button size={size} variant="ghost" onClick={() => { setNote(''); resolve.reset(); setOpen(true); }}><Icono nombre="mdi:check" /> Marcar como resuelto</Button>
      </span>
      <Dialog open={other} onOpenChange={setOther}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Reenviar a otro correo</DialogTitle>
            <DialogDescription>El mismo correo, con sus adjuntos, sale a la dirección que escribas. El correo del ciudadano no cambia; en el expediente y en la auditoría queda que se envió a otra dirección.</DialogDescription>
          </DialogHeader>
          <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); if (parsed.success && !same) retry.mutate({ id, to: parsed.data.to }, { onSuccess: (r) => { setOther(false); retried(r); } }); }}>
            <p className="text-sm m-0 break-all"><span className="text-muted-foreground">Iba a:</span> {to.join(', ') || '—'}</p>
            <Field label="Nuevo correo" htmlFor={`retry-to-${id}`} error={address.trim() && !parsed.success ? 'Escribe un correo válido' : same ? 'Es la misma dirección: usa «Reenviar»' : undefined}><Input id={`retry-to-${id}`} type="email" autoFocus value={address} placeholder="nombre@correo.com" onChange={(e) => setAddress(e.target.value)} maxLength={320} /></Field>
            {retry.isError && <Alert variant="destructive"><AlertDescription>{errorMessage(retry.error)}</AlertDescription></Alert>}
            <DialogFooter><Button type="button" variant="ghost" onClick={() => setOther(false)}>Cancelar</Button><Button type="submit" disabled={!address.trim() || !parsed.success || same || retry.isPending}>{retry.isPending ? <Spinner /> : <Icono nombre="mdi:send-outline" />} Reenviar</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Marcar como resuelto</DialogTitle>
            <DialogDescription>El correo no se reenvía. Cuenta cómo le llegó la información al ciudadano: queda en el expediente.</DialogDescription>
          </DialogHeader>
          <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); resolve.mutate({ id, note }, { onSuccess: () => { setOpen(false); avisar.success('Marcado como resuelto'); } }); }}>
            <Field label="Cómo se resolvió" htmlFor={`resolve-${id}`}><Textarea id={`resolve-${id}`} rows={3} value={note} autoFocus placeholder="Ej.: se le llamó y se le envió desde otro correo" onChange={(e) => setNote(e.target.value)} /></Field>
            {resolve.isError && <Alert variant="destructive"><AlertDescription>{errorMessage(resolve.error)}</AlertDescription></Alert>}
            <DialogFooter><Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button><Button type="submit" disabled={note.trim().length < 3 || resolve.isPending}>{resolve.isPending ? <Spinner /> : <Icono nombre="mdi:check" />} Marcar como resuelto</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
